import { html, render, nothing, type TemplateResult } from 'lit-html';
import styles from './pk-key-table.css?inline';
import { formatUtc, parseJsonArray } from '../../lib/portal-data.js';
import {
    custodyLabel, fillKeyUrl, keyName, kindLabel, stateKind, stateLabel, type PkKeyRecord,
} from '../../lib/keys.js';
import { formFields, requestThroughGate } from '../../lib/step-up.js';
import '../pk-button/pk-button.js';
import '../pk-status-pill/pk-status-pill.js';

/** The row actions the table can render. */
export type PkKeyRowAction = 'rotate' | 'disable' | 'enable' | 'revoke' | 'staff-revoke' | 'override';

const DEFAULT_ACTIONS: Record<PkKeyRowAction, string> = {
    rotate: '/portal/admin/keys/{id}/rotate',
    disable: '/portal/admin/keys/{id}/disable',
    enable: '/portal/admin/keys/{id}/enable',
    revoke: '/portal/admin/keys/{id}/revoke',
    'staff-revoke': '/portal/staff/keys/{id}/revoke',
    override: '/portal/staff/keys/{id}/destroy-requests/{requestId}/override',
};

const LABELS: Record<PkKeyRowAction, string> = {
    rotate: 'Rotate',
    disable: 'Disable',
    enable: 'Enable',
    revoke: 'Revoke',
    'staff-revoke': 'Force-revoke',
    override: 'Override destroy',
};

/**
 * The actions a viewer may take on a key, by role and state (PortalKit keys spec §3): tenant Owners rotate
 * (provider / BYOK, active), disable (active / retired), enable (disabled) and revoke (active / retired / disabled);
 * Operators read; staff force-revoke and override a pending destroy. Destroyed keys have none.
 */
export function rowActions(key: PkKeyRecord, roleLevel: number, isStaff: boolean): PkKeyRowAction[] {
    if (key.state === 'destroyed') return [];
    if (isStaff) {
        if (key.state === 'destroy-pending') return key.pendingDestroy ? ['override'] : [];
        return ['active', 'retired', 'disabled'].includes(key.state) ? ['staff-revoke'] : [];
    }
    if (roleLevel < 4) return [];
    const actions: PkKeyRowAction[] = [];
    if (key.state === 'active' && (key.custody === 'provider' || key.custody === 'byok')) actions.push('rotate');
    if (key.state === 'active' || key.state === 'retired') actions.push('disable');
    if (key.state === 'disabled') actions.push('enable');
    if (['active', 'retired', 'disabled'].includes(key.state)) actions.push('revoke');
    return actions;
}

/**
 * `<pk-key-table>` — a tenant's keys (or, for staff, every tenant's): name / thumbprint, kind, custody pill,
 * state pill, expiry, escrow, a pending-destroy banner per row (requester + expiry) and the row actions the
 * viewer's role allows (`rowActions`).
 *
 * Row actions are form POSTs (with the anti-forgery field). Inside a `<pk-step-up-gate>` they go through it — so a
 * `403 step-up-required` opens the TOTP prompt and the action is re-sent with the proof; without a gate they post
 * natively. Staff force-revoke and override carry a required `reason`.
 *
 * Attributes:
 *   - `keys`        — JSON array of keys (contract `Key` / `AdminKey`); the `keys` property wins once set.
 *   - `role-level`  — the viewer's role level 0–4 (Owner = 4 gets the mutations).
 *   - `is-staff`    — boolean; staff actions (force-revoke, override) instead of the Owner ones.
 *   - `show-tenant` — boolean; adds a Tenant column (staff lists).
 *   - `detail-url`  — link template for the name, `{id}` replaced (default none).
 *   - `rotate-action`, `disable-action`, `enable-action`, `revoke-action`, `staff-revoke-action`,
 *     `override-action` — POST URL templates (`{id}`, `{requestId}`); defaults under `/portal/admin/keys` and
 *     `/portal/staff/keys`.
 *   - `empty-text`  — text when there are no keys (default `No keys yet.`).
 *   - `csrf-token` / `csrf-name` — anti-forgery hidden input in every form.
 *
 * Events:
 *   - `pk-key-action` — `CustomEvent<{ action, keyId, requestId? }>`, cancelable, as a row action submits.
 */
export class PkKeyTable extends HTMLElement {
    static readonly tagName = 'pk-key-table';

    static get observedAttributes(): string[] {
        return [
            'keys', 'role-level', 'is-staff', 'show-tenant', 'detail-url', 'empty-text', 'csrf-token', 'csrf-name',
            'rotate-action', 'disable-action', 'enable-action', 'revoke-action', 'staff-revoke-action', 'override-action',
        ];
    }

    private readonly _root: ShadowRoot;
    private _keys: PkKeyRecord[] | null = null;

    constructor() {
        super();
        this._root = this.attachShadow({ mode: 'open' });
    }

    connectedCallback(): void {
        this._root.addEventListener('submit', this._onSubmit);
        this._render();
    }

    disconnectedCallback(): void {
        this._root.removeEventListener('submit', this._onSubmit);
    }

    attributeChangedCallback(name: string): void {
        if (name === 'keys') this._keys = null;
        if (this.isConnected) this._render();
    }

    /** The keys (property wins over the attribute once set). */
    get keys(): PkKeyRecord[] {
        return this._keys ?? parseJsonArray<PkKeyRecord>(this.getAttribute('keys'));
    }

    set keys(value: PkKeyRecord[]) {
        this._keys = Array.isArray(value) ? value : [];
        if (this.isConnected) this._render();
    }

    private get _roleLevel(): number {
        const n = Number(this.getAttribute('role-level') ?? '0');
        return Number.isFinite(n) ? Math.trunc(n) : 0;
    }

    private _actionUrl(action: PkKeyRowAction, key: PkKeyRecord): string {
        const template = this.getAttribute(`${action}-action`) ?? DEFAULT_ACTIONS[action];
        return fillKeyUrl(template, key.id, key.pendingDestroy?.requestId ?? '');
    }

    private readonly _onSubmit = (e: Event): void => {
        const form = e.target as HTMLFormElement;
        if (!form.classList.contains('row-action')) return;
        const action = form.dataset.action as PkKeyRowAction;
        const keyId = form.dataset.keyId ?? '';
        const requestId = form.dataset.requestId || undefined;
        const proceed = this.dispatchEvent(new CustomEvent('pk-key-action', {
            bubbles: true, composed: true, cancelable: true, detail: { action, keyId, ...(requestId ? { requestId } : {}) },
        }));
        if (!proceed) {
            e.preventDefault();
            return;
        }
        const routed = requestThroughGate(this, {
            url: form.getAttribute('action') ?? '',
            method: 'POST',
            fields: formFields(form),
            label: `${LABELS[action]} ${keyId}`,
        });
        if (routed) e.preventDefault();
    };

    private _csrf(): TemplateResult | typeof nothing {
        const token = this.getAttribute('csrf-token') ?? '';
        const name = this.getAttribute('csrf-name') ?? '__RequestVerificationToken';
        return token ? html`<input type="hidden" name=${name} .value=${token} />` : nothing;
    }

    private _actionForm(action: PkKeyRowAction, key: PkKeyRecord): TemplateResult {
        const needsReason = action === 'staff-revoke' || action === 'override';
        const danger = action === 'revoke' || action === 'staff-revoke' || action === 'override';
        return html`
            <form class="row-action ${action}" method="post" action=${this._actionUrl(action, key)}
                  data-action=${action} data-key-id=${key.id} data-request-id=${key.pendingDestroy?.requestId ?? ''}>
                ${this._csrf()}
                ${needsReason ? html`<input class="reason" name="reason" required maxlength="500" placeholder="Reason (required)" aria-label="Reason" />` : nothing}
                <pk-button variant=${danger ? 'danger' : 'secondary'} type="submit">${LABELS[action]}</pk-button>
            </form>
        `;
    }

    private _row(key: PkKeyRecord, isStaff: boolean, showTenant: boolean, detailUrl: string): TemplateResult {
        const actions = rowActions(key, this._roleLevel, isStaff);
        const name = keyName(key);
        const secondary = key.thumbprint && key.thumbprint !== name ? key.thumbprint : key.id !== name ? key.id : '';
        const columns = 6 + (showTenant ? 1 : 0) + 1;
        return html`
            <tr class="key-row" data-key-id=${key.id} data-state=${key.state}>
                ${showTenant ? html`<td class="tenant">${key.tenantId ?? '—'}</td>` : nothing}
                <td class="name">
                    ${detailUrl ? html`<a href=${fillKeyUrl(detailUrl, key.id)}>${name}</a>` : html`<span>${name}</span>`}
                    ${secondary ? html`<span class="id">${secondary}</span>` : nothing}
                </td>
                <td class="kind">${kindLabel(key.kind)}</td>
                <td class="custody"><pk-status-pill kind=${key.custody === 'provider' ? 'info' : 'neutral'}>${custodyLabel(key.custody)}</pk-status-pill></td>
                <td class="state"><pk-status-pill kind=${stateKind(key.state)} dot>${stateLabel(key.state)}</pk-status-pill></td>
                <td class="not-after">${key.notAfter ? formatUtc(key.notAfter) : '—'}</td>
                <td class="escrow">${key.hasEscrow ? 'Yes' : '—'}</td>
                <td class="actions">${actions.map((a) => this._actionForm(a, key))}</td>
            </tr>
            ${key.pendingDestroy && key.state === 'destroy-pending' ? html`
                <tr class="pending-banner" data-key-id=${key.id}>
                    <td colspan=${String(columns)} role="status">
                        Destroy requested by <strong class="requested-by">${key.pendingDestroy.requestedBy}</strong>
                        — a second Owner must approve by <span class="expires">${formatUtc(key.pendingDestroy.expiresAtUtc)}</span>, or it expires.
                    </td>
                </tr>
            ` : nothing}
            ${key.state === 'destroyed' && key.tombstone ? html`
                <tr class="tombstone-banner" data-key-id=${key.id}>
                    <td colspan=${String(columns)}>
                        Destroyed ${formatUtc(key.destroyedAtUtc)} —
                        ${key.tombstone.override
                            ? html`override by <strong>${key.tombstone.destroyedBy}</strong>`
                            : html`requested by <strong>${key.tombstone.destroyedBy}</strong>, approved by <strong>${key.tombstone.approvedBy ?? '—'}</strong>`}.
                    </td>
                </tr>
            ` : nothing}
        `;
    }

    private template(): TemplateResult {
        const keys = this.keys;
        const isStaff = this.hasAttribute('is-staff');
        const showTenant = this.hasAttribute('show-tenant');
        const detailUrl = this.getAttribute('detail-url') ?? '';
        if (keys.length === 0) {
            return html`<style>${styles}</style><p class="empty">${this.getAttribute('empty-text') ?? 'No keys yet.'}</p>`;
        }
        return html`
            <style>${styles}</style>
            <table>
                <thead>
                    <tr>
                        ${showTenant ? html`<th scope="col">Tenant</th>` : nothing}
                        <th scope="col">Key</th>
                        <th scope="col">Kind</th>
                        <th scope="col">Custody</th>
                        <th scope="col">State</th>
                        <th scope="col">Expires</th>
                        <th scope="col">Escrow</th>
                        <th scope="col"><span class="visually-hidden">Actions</span></th>
                    </tr>
                </thead>
                <tbody>
                    ${keys.map((k) => this._row(k, isStaff, showTenant, detailUrl))}
                </tbody>
            </table>
        `;
    }

    private _render(): void {
        render(this.template(), this._root);
    }
}

if (!customElements.get(PkKeyTable.tagName)) {
    customElements.define(PkKeyTable.tagName, PkKeyTable);
}

declare global {
    interface HTMLElementTagNameMap {
        'pk-key-table': PkKeyTable;
    }
}
